import Box from '@mui/material/Box'

import { LoadedLink } from '$components/common'
import { logger } from '$utils'

import useActiveModule from './useActiveModule'
import useApplicationMenu, {
  ApplicationMenuSection,
} from './useApplicationMenu'
import { getModuleNavTarget, getModuleSegment } from './helpers'

/**
 * Determines whether a module link points at the external admin site rather
 * than an in-app hash route. The AngularJS topbar special-cases the `Admin`
 * entry to open in a new tab, so we reproduce that behaviour.
 */
function isExternalModuleLink(section: ApplicationMenuSection): boolean {
  return section.label === 'Admin'
}

/**
 * The main module navigation for the Loaded app which is shown in the topbar.
 */
const TopbarModuleNav = () => {
  const { data: sections, isPending, isError } = useApplicationMenu()
  const activeModuleSegment = useActiveModule()

  // show nothing until we have results to display
  if (isPending) {
    return null
  }

  // The topbar must never block the page on a menu failure - the rest of the
  // app shell stays usable, so we render no module nav and log for diagnosis.
  if (isError || !sections) {
    logger.error('Topbar could not load the application menu')
    return null
  }

  return (
    <Box
      component="nav"
      display="flex"
      alignItems="center"
      gap={0.5}
      height="100%"
    >
      {sections.map((section) => {
        const modulePathSegment = getModuleSegment(section.link)
        const isExternal = isExternalModuleLink(section)
        const isActive =
          modulePathSegment !== '' && activeModuleSegment === modulePathSegment

        return (
          <LoadedLink
            key={section.label}
            to={isExternal ? section.link : getModuleNavTarget(section.link)}
            isExternalUrl={isExternal}
            openNewTab={isExternal}
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 30,
              paddingX: 1.5,
              borderRadius: 1,
              // Typography matching the old `#mainnav a` rules in loaded.less
              // (font-size 14px, font-weight 600, letter-spacing 1px,
              // text-transform uppercase). Explicit px (not rem) because the
              // Bootstrap host sets a 10px root font size.
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              '&:focus': {
                // These are required to 'beat' some old style rules set in the
                // AngularJS stylesheet that set default styles on all 'a' tags
                // on the page.
                outline: 'none',
                textDecoration: 'none',
                color: 'initial',
              },
              color: isActive
                ? (theme) => theme.app.headerBackground
                : (theme) => theme.app.headerTextColor,
              backgroundColor: isActive
                ? (theme) => theme.app.headerTextColor
                : 'transparent',
              '&:hover': {
                color: isActive
                  ? (theme) => theme.app.headerBackground
                  : (theme) => theme.app.headerTextColor,
                backgroundColor: isActive
                  ? (theme) => theme.app.headerTextColor
                  : 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {section.label}
          </LoadedLink>
        )
      })}
    </Box>
  )
}

export default TopbarModuleNav
